import { Options, Vue } from 'vue-class-component';
import store from '@/trust-admin-app/store';
import moment from 'moment';
import { Inject } from 'vue-property-decorator';
import { AppInsights } from '@/trust-admin-app/appInsights';
import { TrustAPI } from '@/shared/modules/trust-api';
import { TimeEntryView, TimeEntryApprovalView, TimeEntryType, AdvanceFilter, TimeEntryActivity } from '@/shared/modules/typescript-trustapi-client/models';
import { EventEmitter } from 'events';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import TimesheetCalculationsMixin from '@/shared/mixins/timesheet-calculations';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import { RouteN } from '@/trust-admin-app/constants/router-name';
import TimesheetHeader from '@/shared/components/timesheet-header/index.vue';
import { TrustTeamMemberView } from '@/shared/modules/typescript-trustapi-client/models';
import { EventForm } from '@/trust-admin-app/components/add-event-modal/event-form';
import DateController from '@/shared/components/date-controller/index.vue';

@Options({
  components: {
    LoadingIndicator,
    ErrorIndicator,
    TimesheetHeader,
    DateController,
  },
  mixins: [FormatDateTimeMixin, TimesheetCalculationsMixin]
})
export default class TimesheetApproval extends Vue {
  private ai = new AppInsights;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Inject({ from: 'timeEntryType', default: [] })
  private timeEntryType!: TimeEntryType[];

  @Inject({ from: 'timeEntryActivity', default: [] })
  private timeEntryActivity!: TimeEntryActivity[];

  private showTimesheetApproval = true;

  private loadingTimesheetApproval = true;
  private errorLoadingTimesheetApproval = true;
  private loadingTimeEntries = true;
  private errorLoadingTimeEntries = true;
  private currentDate = moment(new Date);
  private currentDateDisplay = moment(new Date).format('MMMM YYYY');
  private calendarView = '3'; // monthly view by default; 1: day, 2: week, 3: month
  private currentDatePeriodStart = "";
  private currentDatePeriodEnd = "";
  private investigators: TrustTeamMemberView[] = [];
  private supervisors: TrustTeamMemberView[] = [];
  private practices: any[] = [];
  private showAdvFilter = false;
  private filterApplied = false;
  private filterTimeSheetEntries: TimeEntryView[] = [];
  private filteredTimesheetApprovals: TimeEntryApprovalView[] = [];
  private timesheetApprovals: TimeEntryApprovalView[] = []
  private timesheetEntries: TimeEntryView[] = []
  private investigatorsWithUnderclaimedHours = 0;


  private filters: AdvanceFilter = {
    trustTeamMemberId: null,
    supervisorId: null,
    timeEntryActivityId: null,
    practiceName: null,
  }



  private timesheetApprovalHeadings = [
    { text: 'Investigator', value: 'investigator', sortable: false },
    { text: 'Complete time', value: 'completeTime', sortable: false },
    { text: 'Missing time', value: 'missingTime', sortable: false },
    { text: 'Annual/Personal Leave', value: 'leave', sortable: false },
    { text: 'Public holidays', value: 'publicHolidays', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ]

  private timesheetEntryHeadings = [
    { text: 'Date', value: 'date', sortable: false },
    { text: 'Investigator', value: 'investigator', sortable: false },
    { text: 'Name', value: 'name', sortable: false },
    { text: 'Details', value: 'details', sortable: false },
    { text: 'Practice name', value: 'practiceName', sortable: false },
    { text: 'Reference', value: 'reference', sortable: false },
    { text: 'Break', value: 'break', sortable: false },
    { text: 'Work hours', value: 'workHours', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ]

  mounted(): void {
    window.scrollTo(0, 0);
    this.eventCtrl.on('reload-events-data', this.getAllTimeEntries);
    this.getAllTimeEntries();
    this.getAllTimesheetApprovals();
    this.loadSupervisors();
    this.loadInvestigators();
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('reload-events-data', this.getAllTimeEntries);
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private showHideTSApproval() {
    if (this.showTimesheetApproval) {
      this.showTimesheetApproval = false
    } else {
      this.showTimesheetApproval = true
    }
  }

  private getInvestigatorsWithUnderclaimedHours(timesheetApprovals: TimeEntryApprovalView[]): void {
    this.investigatorsWithUnderclaimedHours = timesheetApprovals.filter(x => x.timesheetStatus?.id === 1).length
  }

  private getAllTimeEntries(): void {
    this.apiService.TimeEntryApi?.apiV2TimeEntryGetTimeEntriesPost({
      advanceFilter: {
        startDate: this.currentDatePeriodStart,
        endDate: this.currentDatePeriodEnd,
      }
    })
      .then(res => {
        this.loadingTimeEntries = false;
        this.errorLoadingTimeEntries = false;
        this.timesheetEntries = res.data.data;
        this.filterTimeSheetEntries = this.timesheetEntries;
        this.practices = this.getPractices();
      })
      .catch(error => {
        this.loadingTimeEntries = false;
        this.errorLoadingTimeEntries = true;
        this.eventCtrl.emit('show-snackbar', 'error', 'Error getting time entries - ' + error);
        this.ai.trackException('Error getting time entries: ' + error);
      });
  }

  private getAllTimesheetApprovals(): void {
    this.apiService.TimeEntryApi?.apiV2TimeEntryGetTimeEntryApprovalsPost({
      startDate: this.currentDatePeriodStart,
      endDate: this.currentDatePeriodEnd,
    })
      .then(res => {
        this.loadingTimesheetApproval = false;
        this.errorLoadingTimesheetApproval = false
        this.timesheetApprovals = res.data.data;
        this.filteredTimesheetApprovals = this.timesheetApprovals;
        this.getInvestigatorsWithUnderclaimedHours(this.timesheetApprovals);
      })
      .catch(error => {
        this.loadingTimesheetApproval = false;
        this.errorLoadingTimesheetApproval = true;
        this.eventCtrl.emit('show-snackbar', 'error', 'Error getting timesheet approvals - ' + error);
        this.ai.trackException('Error getting timesheet approvals : ' + error);
      })
  }

  private approveTimeEntries(trustTeamMember: TrustTeamMemberView): void {
    this.apiService.TimeEntryApi?.apiV2TimeEntryApproveTimeEntriesPost({
      trustMemberId: trustTeamMember.id,
      startDate: this.currentDatePeriodStart,
      endDate: this.currentDatePeriodEnd
    })
      .then(() => {
        // console.log(res.data.data);
      })
      .catch(error => {
        this.eventCtrl.emit('show-snackbar', 'error', error);
        this.ai.trackException('Error approving investigator timesheet: ' + error);
      })
  }

  get RouteN(): typeof RouteN {
    return RouteN;
  }

  private viewInvestigator(investigator: TrustTeamMemberView): void {
    this.$router.push({ name: RouteN.TA.InvestigatorTimesheets.n, query: { memberId: investigator.id } });
  }

  private getTimePeriod(options: any): void {
    this.currentDatePeriodStart = options.startDatePeriod;
    this.currentDatePeriodEnd = options.endDatePeriod;
    this.getAllTimeEntries();
    this.getAllTimesheetApprovals();
  }

  private prev(value: any): void {
    this.currentDate = value;
  }

  private next(value: any): void {
    this.currentDate = value;
  }

  private today(options: any): void {
    this.calendarView = options.calendarView;
    this.currentDate = options.currentDate;
  }

  private updateDisplay(value: any): void {
    this.currentDateDisplay = value;
  }

  private applyFilter(): void {
    this.filterApplied = true;

    this.filterTimeSheetEntries = this.timesheetEntries;
    this.filteredTimesheetApprovals = this.timesheetApprovals;

    if (this.filters.practiceName) {
      this.filterTimeSheetEntries = this.filterTimeSheetEntries.filter(
        x => x.investigation?.latestPracticeLogData.firmName == this.filters.practiceName
      );
    }

    if (this.filters.timeEntryActivityId) {
      this.filterTimeSheetEntries = this.filterTimeSheetEntries.filter(
        x => x.timeEntryActivity?.id == this.filters.timeEntryActivityId
      );
    }

    if (this.filters.trustTeamMemberId) {
      this.filterTimeSheetEntries = this.filterTimeSheetEntries.filter(
        x => x.trustTeamMember.id == this.filters.trustTeamMemberId
      );

      this.filteredTimesheetApprovals = this.filteredTimesheetApprovals.filter(
        x => x.trustTeamMember.id == this.filters.trustTeamMemberId
      );

    }

    if (this.filters.supervisorId) {
      this.filterTimeSheetEntries = this.filterTimeSheetEntries.filter(
        x => x.trustTeamMember?.supervisor == this.filters.supervisorId
      );

      this.filteredTimesheetApprovals = this.filteredTimesheetApprovals.filter(
        x => x.trustTeamMember?.supervisor == this.filters.supervisorId
      );
    }
  }

  private resetFilter(): void {
    this.filterApplied = false;
    this.filters = {
      trustTeamMemberId: null,
      supervisorId: null,
      timeEntryActivityId: null,
      practiceName: null,
    };
    this.filterTimeSheetEntries = this.timesheetEntries;
    this.filteredTimesheetApprovals = this.timesheetApprovals;
  }

  private addEntry(): void {
    this.eventCtrl.emit('showAddEditEventModal', { eventTypeId: 1, trustTeamMemberId: this.selectedTrustTeamMember.id });
  }

  private editTimesheet(timeEntry: TimeEntryView): void {
    const eventFormData: EventForm = {
      id: timeEntry.id,
      eventTypeId: timeEntry.timeEntryType?.id ?? null,
      leaveTypeId: timeEntry.leaveType?.id ?? null,
      startDateTime: timeEntry.startDateTime,
      endDateTime: timeEntry.endDateTime,
      notes: timeEntry.notes ?? null,
      activityType: timeEntry.timeEntryActivity ?? null,
      investigation: timeEntry.investigation ?? null,
      breakDuration: timeEntry.breakDuration ?? null,
      trustTeamMember: timeEntry.trustTeamMember ?? null,
      trustTeamMemberId: timeEntry.trustTeamMember.id ?? null,
      description: timeEntry.description ?? null,
    };
    this.eventCtrl.emit('showAddEditEventModal', eventFormData);
  }

  // get investigators(): TrustTeamMemberView[] {
  //   const investigators: TrustTeamMemberView[] = [];
  //   this.timesheetEntries.forEach((x) => {
  //     if(investigators.filter(i => x.trustTeamMember.id == i.id).length == 0) {
  //       investigators.push(x.trustTeamMember);
  //     }
  //   });
  //   return investigators;
  // }

  private getPractices(): any[] {
    const practices: any[] = [];
    this.timesheetEntries.forEach((x) => {
      if ((x.investigation) && (practices.filter(p => x.investigation?.latestPracticeLogData.practiceId == p.practiceId).length == 0)) {
        practices.push(x.investigation.latestPracticeLogData);
      }
    });
    return practices;
  }

  private async loadInvestigators(): Promise<void> {
    try {
      const res = await this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetTrustTeamMembersPost();
      this.investigators = res?.data.data as TrustTeamMemberView[];
    } catch (error) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on loading investigators - ' + error);
      this.ai.trackException('Error on loading investigators: ' + error);
    }
  }

  private async loadSupervisors(): Promise<void> {
    try {
      const res = await this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetSupervisorsGet();
      this.supervisors = res?.data.data as TrustTeamMemberView[];
    } catch (error) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error on loading supervisors - ' + error);
      this.ai.trackException('Error on loading supervisors: ' + error);
    }
  }

  private showHideAdvFilter() {
    this.showAdvFilter = !this.showAdvFilter;
  }

  get selectedTrustTeamMember(): TrustTeamMemberView {
    return store.getters.getSelectedTrustTeamMember;
  }
}

